import { useEffect, useMemo, useState } from 'react';

import { useModalForm } from '../../../../foundation/hooks';
import {
  useGetVariantAssessmentArgumentsQuery,
  useUpdateVariantAssessmentArgumentsMutation,
} from '../../../api/variantApi';
import { VARIANT_STAGE_TYPES } from '../../constants';
import {
  handleArgumentSelection,
  useArgumentsTableColumns,
} from '../helpers/argumentsHelpers';
import { StructuredArgument } from '../helpers/structureArgumentsData';

interface UseArgumentsClassificationModalLogicProps {
  closeModal: () => void;
  dataSource: StructuredArgument[];
  variantId: string;
}

/**
 * Custom hook that encapsulates all the business logic for ArgumentsClassificationModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 *
 * Per the RTK Query tutorial:
 * "You should normally use the query hooks to access cached data in components -
 * you shouldn't write your own useSelector calls to access fetched data."
 * If you need the same data in multiple components, just call the same query hook
 * with the same arguments in each component! RTK Query will make sure the data
 * is only fetched once, and each component will re-render as needed.
 */
export const useArgumentsClassificationModalLogic = ({
  closeModal,
  dataSource,
  variantId,
}: UseArgumentsClassificationModalLogicProps) => {
  const [updateVariantAssessmentArgumentsMutation] =
    useUpdateVariantAssessmentArgumentsMutation();

  // Per the tutorial: Calling the query hook automatically fetches and caches data!
  // The hook returns a result object containing data, isLoading, isSuccess, etc.
  const { data: variantArgumentsForClassification } =
    useGetVariantAssessmentArgumentsQuery(
      { variant_id: variantId },
      { skip: !variantId },
    );

  const {
    isLoading: isSaving,
    setIsLoading: setIsSaving,
    handleModalClose,
    createModalOpts,
  } = useModalForm({
    onClose: closeModal,
    successMessage: 'Arguments updated successfully',
    stageType: VARIANT_STAGE_TYPES.INTERPRETATION,
  });

  const [isArgumentModalVisible, setArgumentModalVisibility] = useState(false);
  const [selectedArgument, setSelectedArgument] =
    useState<StructuredArgument | null>(null);

  const [internalSource, setInternalSource] =
    useState<StructuredArgument[]>(dataSource);

  useEffect(() => {
    setInternalSource(dataSource);
  }, [dataSource]);

  const modalOpts = {
    ...createModalOpts('Arguments for classification: overview'),
  };

  const handleArgumentEditClick = (record: StructuredArgument) => {
    setSelectedArgument(record);
    setArgumentModalVisibility(true);
  };

  const columns = useArgumentsTableColumns(
    true,
    true,
    (record) => handleArgumentSelection(record, setInternalSource),
    handleArgumentEditClick,
  );

  const hasChanges = useMemo(() => {
    // Filter out stage rows from dataSource
    const filteredDataSource = internalSource.filter(
      (item) => !item.isStageRow,
    );

    // Quick length check - fastest possible early return
    if (
      filteredDataSource.length !==
      (variantArgumentsForClassification ?? []).length
    ) {
      return true;
    }

    // Create a map of the original arguments for quick lookup
    const originalArgsMap = new Map(
      (variantArgumentsForClassification ?? []).map((arg) => [arg.id, arg]),
    );

    // Compare each argument in dataSource with its original version
    // Only check selected and comment fields since those are the only fields that can change
    for (const newArg of filteredDataSource) {
      const originalArg = originalArgsMap.get(newArg.id as number);

      // If argument doesn't exist in original, there's a change
      if (!originalArg) {
        return true;
      }

      // Only compare the fields that can actually change
      if (
        newArg.selected !== originalArg.selected ||
        newArg.comment !== originalArg.comment ||
        (newArg.is_argument_met ?? true) !==
          (originalArg.is_argument_met ?? true)
      ) {
        return true;
      }
    }

    return false;
  }, [internalSource, variantArgumentsForClassification, dataSource]);

  const handleSaveChanges = async () => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      const argumentsToSave = internalSource.filter((arg) => {
        if (arg.isStageRow) {
          return false;
        }
        const hasComment =
          arg.comment && arg.comment.trim() !== '' && arg.comment !== '<p></p>';
        return arg.selected || hasComment;
      });

      const requestData = {
        argumentdata: argumentsToSave.map((arg) => ({
          argument_id: arg.id as number,
          comment: arg.comment as string,
          is_argument_met: arg.is_argument_met ?? true,
        })),
      };

      await updateVariantAssessmentArgumentsMutation({
        variant_id: variantId,
        requestData,
      }).unwrap();

      // RTK Query automatically refetches VariantArguments + VariantStages + InterpretationZone tags

      handleModalClose();
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const closeArgumentModal = () => {
    setArgumentModalVisibility(false);
  };

  return {
    isSaving,
    isArgumentModalVisible,
    selectedArgument,
    internalSource,
    setInternalSource,
    modalOpts,
    columns,
    hasChanges,
    handleSaveChanges,
    closeArgumentModal,
  };
};

export default useArgumentsClassificationModalLogic;
